// Media commands — the shared photo index (shared/mediadoc.js, docs/SYNC.md `media`). Pure; see tasks.js for the ctx
// shape. ctx = { media, tasks, projects, now, newId, uid }. One doc per picture: adding is one `set` each (two phones
// shooting the same job never collide), editing is one `update` of the doc, removing is one `delete` (the bytes stay in
// the store — a stranded file costs disk, not correctness).
import { set, update, del } from './writes.js';
import { newMediaDoc, mediaFromShots } from '../mediadoc.js';
import { PHOTO_TAGS, taskPhotoList } from '../taskphotos.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const needMedia = (ctx, id) => { const m = (ctx.media || []).find((x) => x.id === id); if (!m) throw new Error(`no media ${id}`); return m; };

/**
 * New media docs. input = { items: [{ uri, kind?, source?, projectId?, taskId?, tag?, takenAt?, caption?, estimateId?,
 * itemId?, thumbUri?, contentType?, w?, h?, size?, durationMs? }] }. A task item fills projectId from the task.
 */
export function addMedia(ctx, { items = [] }) {
  const at = now(ctx);
  const docs = items.filter((x) => x && x.uri).map((x, i) => {
    const task = x.taskId ? (ctx.tasks || []).find((t) => t.id === x.taskId) : null;
    return newMediaDoc({ ...x, id: x.id || ctx.newId(), at: at + i, by: x.by || ctx.uid || null, projectId: x.projectId || task?.projectId || null });
  });
  return { writes: docs.map((d) => set(`media/${d.id}`, d)), media: docs };
}

/** A task's new shots ([{ uri, tag, takenAt?, caption? }]) as media docs — what addTaskShots callers write beside the task patch. */
export function addTaskMedia(ctx, { taskId, shots = [], cover = false }) {
  const task = (ctx.tasks || []).find((t) => t.id === taskId);
  if (!task) throw new Error(`no task ${taskId}`);
  const docs = mediaFromShots(task, shots, { newId: ctx.newId, now: now(ctx), by: ctx.uid || null, cover });
  return { writes: docs.map((d) => set(`media/${d.id}`, d)), media: docs };
}

/** Edit one photo: { id, caption?, tag?, cover?, taskId?, onProposal?, transcript?, takenAt? }. Empty caption clears it. */
export function updateMedia(ctx, { id, ...patch }) {
  const m = needMedia(ctx, id);
  const d = {};
  if ('caption' in patch) d.caption = String(patch.caption ?? '').trim() || null;
  if ('tag' in patch) { if (patch.tag != null && !PHOTO_TAGS.includes(patch.tag)) throw new Error(`tag must be one of ${PHOTO_TAGS.join(', ')}`); d.tag = patch.tag ?? null; }
  if ('cover' in patch) d.cover = !!patch.cover;
  if ('onProposal' in patch) d.onProposal = !!patch.onProposal;
  if ('transcript' in patch) { d.transcript = String(patch.transcript ?? '').trim() || null; d.transcriptStatus = d.transcript ? 'done' : m.transcriptStatus || 'pending'; }
  if ('takenAt' in patch && Number(patch.takenAt) > 0) d.takenAt = Number(patch.takenAt);
  if ('taskId' in patch) {   // file it under a task (or back to the project): the project follows the task
    const t = patch.taskId ? (ctx.tasks || []).find((x) => x.id === patch.taskId) : null;
    if (patch.taskId && !t) throw new Error(`no task ${patch.taskId}`);
    d.taskId = t ? t.id : null;
    if (t) { d.projectId = t.projectId || m.projectId || null; d.source = 'task'; if (!m.tag) d.tag = 'progress'; }
    else if (m.source === 'task') d.source = 'project';
  }
  if (!Object.keys(d).length) throw new Error('nothing to change');
  d.updatedAt = now(ctx);
  return { writes: [update(`media/${id}`, d)], media: { ...m, ...d } };
}

/** Remove one photo from the index (the file stays in the store). */
export function removeMedia(ctx, { id }) {
  needMedia(ctx, id);
  return { writes: [del(`media/${id}`)] };
}

/**
 * Keep the index in step with a task patch while task.shots / photo / afterPhotos are still written (dual-write):
 * taskPhotoList(before) vs taskPhotoList(after), matched by uri.
 *   new pictures   → `creates`: media docs (uri = the picture as the task holds it); the phone keeps a durable copy
 *                    and sets the doc itself, the web sets them as they are
 *   same picture   → `writes`: an update when its tag / caption / cover changed
 *   gone pictures  → `writes`: a delete
 * ctx.media = this task's media docs. after = the task as it will be stored; before = as it was (null when new).
 */
export function reconcileTaskMedia(ctx, { before, after }) {
  if (!after?.id) return { creates: [], writes: [] };
  const was = new Map(taskPhotoList(before).map((p) => [p.uri, p]));
  const now_ = taskPhotoList(after);
  const docs = (ctx.media || []).filter((m) => m && m.taskId === after.id);
  const byUri = new Map(docs.map((m) => [m.uri, m]));
  const at = now(ctx);
  const creates = [];
  const writes = [];
  now_.forEach((p, i) => {
    const m = byUri.get(p.uri);
    if (!m) {
      if (!was.has(p.uri) || !docs.length) creates.push(newMediaDoc({
        id: ctx.newId(), uri: p.uri, source: 'task', projectId: after.projectId || null, taskId: after.id, tag: p.tag,
        takenAt: p.takenAt || p.at || null, at: at + i, by: ctx.uid || null, caption: p.caption, cover: p.cover,
      }));
      return;
    }
    const d = {};
    if ((m.tag || null) !== p.tag) d.tag = p.tag;
    if ((m.caption || null) !== (p.caption || null)) d.caption = p.caption || null;
    if (!!m.cover !== !!p.cover) d.cover = !!p.cover;
    if ((m.projectId || null) !== (after.projectId || null)) d.projectId = after.projectId || null;
    if (Object.keys(d).length) writes.push(update(`media/${m.id}`, { ...d, updatedAt: at }));
  });
  const keep = new Set(now_.map((p) => p.uri));
  for (const m of docs) if (!keep.has(m.uri)) writes.push(del(`media/${m.id}`));
  return { creates, writes };
}
